import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ApiService from "../services/ApiService";

interface UpdateComponentsJob {
  scriptExecutionId: string;
  systemDomainId: string;
  label: string;
  status: "running" | "complete" | "error";
  results?: {
    total: number;
    errorCount: number;
    errors: Record<string, string>;
  } | null;
  error?: string;
  startedAt: number;
}

interface ScriptExecutionResult {
  data: {
    scriptExecution: {
      status: "waiting" | "running" | "retrying" | "error" | "success";
      messages: { message: string; date: string }[];
      metadata?: {
        total?: number;
        errorCount?: number;
        errors?: Record<string, string>;
      } | null;
    };
  };
}

interface UpdateComponentsJobContextValue {
  jobs: UpdateComponentsJob[];
  addComponentsJob: (
    scriptExecutionId: string,
    systemDomainId: string,
    label: string
  ) => void;
  dismissJob: (scriptExecutionId: string) => void;
  // dev only
  simulateJob: (label: string, errors?: Record<string, string>) => void;
}

const UpdateComponentsJobContext =
  createContext<UpdateComponentsJobContextValue>({
    jobs: [],
    addComponentsJob: () => undefined,
    dismissJob: () => undefined,
    simulateJob: () => undefined,
  });

export const useUpdateComponentsJob = () =>
  useContext(UpdateComponentsJobContext);

export const UpdateComponentsJobProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [jobs, setJobs] = useState<UpdateComponentsJob[]>([]);
  const dismissTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );

  const addComponentsJob = useCallback(
    (scriptExecutionId: string, systemDomainId: string, label: string) => {
      if (!scriptExecutionId || !systemDomainId) {
        return;
      }
      setJobs((prev) => [
        ...prev,
        {
          scriptExecutionId,
          systemDomainId,
          label,
          status: "running",
          startedAt: Date.now(),
        },
      ]);
    },
    []
  );

  const dismissJob = useCallback((scriptExecutionId: string) => {
    clearTimeout(dismissTimers.current[scriptExecutionId]);
    delete dismissTimers.current[scriptExecutionId];
    setJobs((prev) =>
      prev.filter((j) => j.scriptExecutionId !== scriptExecutionId)
    );
  }, []);

  const simulateJob = useCallback(
    (label: string, errors: Record<string, string> = {}) => {
      const scriptExecutionId = `sim-${Date.now()}`;
      setJobs((prev) => [
        ...prev,
        {
          scriptExecutionId,
          systemDomainId: "sim",
          label,
          status: "running",
          startedAt: Date.now(),
        },
      ]);
      setTimeout(() => {
        const errorCount = Object.keys(errors).length;
        setJobs((prev) =>
          prev.map((j) =>
            j.scriptExecutionId === scriptExecutionId
              ? {
                  ...j,
                  status: errorCount ? "error" : "complete",
                  results: { total: 5, errorCount, errors },
                }
              : j
          )
        );
        if (!errorCount) {
          dismissTimers.current[scriptExecutionId] = setTimeout(() => {
            setJobs((prev) =>
              prev.filter((j) => j.scriptExecutionId !== scriptExecutionId)
            );
            delete dismissTimers.current[scriptExecutionId];
          }, 5000);
        }
      }, 2000);
    },
    []
  );

  useEffect(() => {
    const running = jobs.filter((j) => j.status === "running");
    if (!running.length) {
      return;
    }

    const interval = setInterval(async () => {
      await Promise.all(
        running.map(async (job) => {
          if (job.scriptExecutionId.startsWith("sim-")) {
            return;
          }
          try {
            const res = await ApiService.fetch<ScriptExecutionResult>(
              "GET",
              `/api/script_execution/${job.systemDomainId}/script/${job.scriptExecutionId}`
            );
            const { status, messages, metadata } = res.data.scriptExecution;
            if (status === "success") {
              const results =
                metadata?.total != null
                  ? {
                      total: metadata.total,
                      errorCount: metadata.errorCount ?? 0,
                      errors: metadata.errors ?? {},
                    }
                  : null;
              setJobs((prev) =>
                prev.map((j) =>
                  j.scriptExecutionId === job.scriptExecutionId
                    ? { ...j, status: "complete", results }
                    : j
                )
              );
              if (!results?.errorCount) {
                dismissTimers.current[job.scriptExecutionId] = setTimeout(
                  () => {
                    setJobs((prev) =>
                      prev.filter(
                        (j) => j.scriptExecutionId !== job.scriptExecutionId
                      )
                    );
                    delete dismissTimers.current[job.scriptExecutionId];
                  },
                  5000
                );
              }
            } else if (status === "error") {
              const error =
                messages[messages.length - 1]?.message ?? "Unknown error";
              setJobs((prev) =>
                prev.map((j) =>
                  j.scriptExecutionId === job.scriptExecutionId
                    ? { ...j, status: "error", error }
                    : j
                )
              );
            }
          } catch {
            // ignore transient poll errors
          }
        })
      );
    }, 2000);

    return () => clearInterval(interval);
  }, [jobs]);

  return (
    <UpdateComponentsJobContext.Provider
      value={{ jobs, addComponentsJob, dismissJob, simulateJob }}
    >
      {children}
    </UpdateComponentsJobContext.Provider>
  );
};
